/**
 * The body of a shared workout on the web, one layout per activity type, laid
 * out as the app's SharedWorkoutPreview / SingleWorkoutSummary lay it out
 * (apps/mobile/client/app/src/screens/sharedScreens/SharedWorkoutPreview.tsx).
 * The rowing step wording mirrors apps/mobile/client/app/src/utils/rowing.ts.
 *
 * The viewer here has no Burst profile, so runs show the paces the link
 * carries (with the app's 5:00/km fallback) and rowing shows targets as a %
 * of 2k, as the link has no watts.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, DocumentText, PlayCircle } from './SharedWorkoutIcons';

/* ─────────── types (the shape of GET /saved_workouts/shared/<token>/) ─────────── */

export type MovementRef = {
    id?: number | null;
    exercise?: string | null;
    movement_type?: string | null;
    portrait_video_url?: string | null;
    landscape_video_url?: string | null;
} | null;

type PlannedSet = { reps: number | null };

type PlannedMovement = {
    exercise_name: string | null;
    portrait_video_url: string | null;
    landscape_video_url: string | null;
    movements?: MovementRef;
    sets: PlannedSet[];
};

type ConditioningElement = {
    conditioning_overview: {
        name: string | null;
        notes?: string | null;
        conditioning_details: { exercise: string | null; detail: string | null }[];
    } | null;
};

export type PlannedSection = {
    section_name: string | null;
    section_notes: string | null;
    section_order: number | null;
    movements: PlannedMovement[];
    conditioning_elements?: ConditioningElement[];
};

type MobilitySession = {
    session_type: string | null;
    mobility_details: { duration: string | null; movement_detail: string | null; movements?: MovementRef }[];
};

type HiitSession = {
    structure: string | null;
    hiit_details: { hiit_movements: { exercise_name: string | null; movements?: MovementRef }[] }[];
};

type ErgSession = {
    warmup_distance: number | null;
    cooldown_distance: number | null;
    repeat_type: string | null;
    movement?: MovementRef;
    saved_intervals: {
        repeats: number | null;
        repeat_distance: number | null;
        target_pace: string | null;
        rest_time: number | null;
    }[];
};

type RunningInterval = {
    repeat_variation: number | null;
    repeats: number | null;
    planned_rep_sec: number | null;
    planned_rep_m: number | null;
    planned_rest_sec: number | null;
    planned_rest_m: number | null;
    target_pace_lower: number | null;
    target_pace_upper: number | null;
    repeat_distance: number | null;
    rest_time: number | null;
};

type RunningSession = {
    prescription_mode: string | null;
    planned_warmup_sec: number | null;
    planned_warmup_m: number | null;
    planned_cooldown_sec: number | null;
    planned_cooldown_m: number | null;
    warmup_distance: number | null;
    cooldown_distance: number | null;
    total_distance: number | null;
    suggested_warmup_pace: number | null;
    repeat_type: string | null;
    running_warmup_template: { movements?: MovementRef[] } | null;
    saved_intervals: RunningInterval[];
};

type RowingStep = {
    repeat_variation: number;
    block: string;
    repeats: number;
    planned_rep_sec?: number | null;
    planned_rep_sec_upper?: number | null;
    planned_rep_m?: number | null;
    planned_rest_sec?: number | null;
    target_lower_pct?: number | null;
    target_upper_pct?: number | null;
    hr_lower_pct?: number | null;
    hr_upper_pct?: number | null;
    rate_lower?: number | null;
    rate_upper?: number | null;
    effort_label?: string | null;
    rpe_lower?: number | null;
    rpe_upper?: number | null;
    talk_test?: string | null;
};

type RowingSession = { machine: string; saved_intervals: RowingStep[] };

type Technique = {
    code: string;
    title: string;
    cues: string;
    landscape_video_url: string | null;
    portrait_video_url: string | null;
};

type ReadyToRow = {
    focus: string;
    key_positions: Technique[];
    drills: { order: number; title: string; technique: Technique | null }[];
};

export type SharedWorkoutPayload = {
    name: string | null;
    description: string | null;
    activity_type: string | null;
    duration: number | null;
    build_type: string | null;
    sections: PlannedSection[];
    workout_sections?: PlannedSection[];
    mobility_sessions?: MobilitySession[];
    hiit_sessions?: HiitSession[];
    erg_sessions?: ErgSession[];
    running_sessions?: RunningSession[];
    rowing_sessions?: RowingSession[];
    ready_to_row?: ReadyToRow | null;
};

/** What the video modal plays: a clip, or a technique's cues when it has none. */
export type VideoTarget = { title: string; url: string | null; cues?: string | null };
type OpenVideo = (v: VideoTarget) => void;

/* ─────────── shared bits ─────────── */

function PlayButton({ onClick, label, size = 20, doc = false }: { onClick: () => void; label: string; size?: number; doc?: boolean }) {
    return (
        <button type="button" className="sw-play" onClick={onClick} aria-label={label}>
            {doc ? <DocumentText size={size} /> : <PlayCircle size={size} />}
        </button>
    );
}

function videoOf(m: MovementRef | undefined): string | null {
    return m?.portrait_video_url || m?.landscape_video_url || null;
}

function Empty() {
    return <p className="sw-summary">No structured detail to show. Open Burst for the full session.</p>;
}

/** A starred row as the strength sections list movements, with a play button when there is a clip. */
function StarRow({ name, hint, video, openVideo, bullet = '*' }: {
    name: string; hint?: string | null; video?: VideoTarget | null; openVideo: OpenVideo; bullet?: string;
}) {
    return (
        <div className="sw-row">
            <div className="sw-row-text">
                <span className="sw-row-bullet">{bullet}</span>
                <span className="sw-row-name">
                    {name}
                    {hint ? <span className="sw-row-hint">{`  ${hint}`}</span> : null}
                </span>
            </div>
            {video ? (
                <PlayButton onClick={() => openVideo(video)} label={`${name} video`} doc={!video.url} />
            ) : null}
        </div>
    );
}

function Rows<T>({ items, render, mini = false }: { items: T[]; render: (item: T, i: number) => React.ReactNode; mini?: boolean }) {
    return (
        <>
            {items.map((item, i) => (
                <React.Fragment key={i}>
                    {render(item, i)}
                    {i < items.length - 1 ? <div className={mini ? 'sw-subdivider sw-subdivider--mini' : 'sw-subdivider'} /> : null}
                </React.Fragment>
            ))}
        </>
    );
}

/* ─────────── Gym / Strength / Hyrox ─────────── */

function formatSetsShort(sets: PlannedSet[]): string {
    if (!sets || sets.length === 0) return '';
    const reps = sets.map((s) => s.reps).filter((r): r is number => r != null);
    if (!reps.length) return `${sets.length} sets`;
    if (reps.every((r) => r === reps[0])) return `${sets.length}x${reps[0]}`;
    return reps.join('-');
}

function instructionForGymSection(name: string | null | undefined): string {
    const n = (name || '').trim();
    if (n === 'Warm up') return 'Complete each movement for 30-45 seconds';
    if (n.startsWith('Strong')) return 'Complete all sets with full rest between';
    if (n.startsWith('Build')) return 'Complete all sets with no rest between movements';
    if (n.startsWith('Pump')) return 'Complete 2-3 sets working towards failure';
    if (n.startsWith('Intensive')) return 'Complete 2 sets of 5 reps';
    if (n.startsWith('Extensive')) return 'Complete 2 sets of 15 reps';
    return '';
}

function instructionForHyroxSection(name: string | null | undefined): string {
    const n = (name || '').trim();
    if (n === 'Warm Up A') return 'Progressive 4 min warm up to get the heart rate going';
    if (n === 'Warm Up B') return 'Complete 2 sets with 5-8 reps in each';
    if (n === 'Warm up') return 'Complete 3 rounds of continuous exercise with no rest';
    if (n === 'Strong 1' || n === 'Strong 2') return 'Complete 3 sets with full rest between';
    if (n === 'Build 1' || n === 'Build 2') return 'Complete all sets with no rest between movements';
    return '';
}

function isConditioningSection(name: string | null | undefined): boolean {
    const n = (name || '').trim();
    return n === 'Sweat' || n === 'Core' || n === 'Metcon' || n === 'Conditioning';
}

function MovementRows({ movements, openVideo }: { movements: PlannedMovement[]; openVideo: OpenVideo }) {
    return (
        <Rows
            items={movements}
            render={(mv) => {
                const url = mv.portrait_video_url || videoOf(mv.movements);
                const name = mv.exercise_name || 'Movement';
                return <StarRow name={name} hint={formatSetsShort(mv.sets || [])} video={url ? { title: name, url } : null} openVideo={openVideo} />;
            }}
        />
    );
}

function SectionHead({ section, idx, instruction }: { section: PlannedSection; idx: number; instruction: string }) {
    const isCond = isConditioningSection(section.section_name);
    const overview = (section.conditioning_elements || []).find((c) => c.conditioning_overview)?.conditioning_overview;
    const title = isCond && overview?.name
        ? `${section.section_name}: ${overview.name}`
        : (section.section_name || `Section ${section.section_order ?? idx + 1}`);
    const sub = instruction || (isCond ? overview?.notes : section.section_notes) || '';
    return (
        <>
            <h3 className="sw-section-title">{title}</h3>
            {sub ? <p className="sw-section-sub">{sub}</p> : null}
        </>
    );
}

function GymBody({ sections, openVideo }: { sections: PlannedSection[]; openVideo: OpenVideo }) {
    if (!sections.length) return <Empty />;
    return (
        <>
            {sections.map((section, i) => {
                const overview = (section.conditioning_elements || []).find((c) => c.conditioning_overview)?.conditioning_overview;
                // As in the app: only the literal "Sweat" section lists its conditioning details.
                const details = section.section_name === 'Sweat' ? overview?.conditioning_details || [] : [];
                const fallback = section.movements?.[0];
                const fallbackUrl = fallback ? fallback.portrait_video_url || videoOf(fallback.movements) : null;
                return (
                    <div key={i} className="sw-section-card">
                        <div className="sw-section">
                            <SectionHead section={section} idx={i} instruction={instructionForGymSection(section.section_name)} />
                            {details.length ? (
                                <Rows
                                    items={details}
                                    render={(cd) => (
                                        <StarRow
                                            name={cd.detail || cd.exercise || ''}
                                            video={fallbackUrl ? { title: fallback?.exercise_name || '', url: fallbackUrl } : null}
                                            openVideo={openVideo}
                                        />
                                    )}
                                />
                            ) : (
                                <MovementRows movements={section.movements || []} openVideo={openVideo} />
                            )}
                        </div>
                    </div>
                );
            })}
        </>
    );
}

function HyroxBody({ sections, openVideo }: { sections: PlannedSection[]; openVideo: OpenVideo }) {
    if (!sections.length) return <Empty />;
    return (
        <>
            {sections.map((section, i) => (
                <div key={i} className="sw-section sw-section--spaced">
                    <SectionHead section={section} idx={i} instruction={instructionForHyroxSection(section.section_name)} />
                    <MovementRows movements={section.movements || []} openVideo={openVideo} />
                </div>
            ))}
        </>
    );
}

/* ─────────── Mobility / HIIT ─────────── */

function MobilityBody({ sessions, openVideo }: { sessions: MobilitySession[]; openVideo: OpenVideo }) {
    if (!sessions.length) return <Empty />;
    const first = sessions[0];
    const count = first.mobility_details?.length || 0;
    const firstDuration = first.mobility_details?.[0]?.duration;
    const summary = !count
        ? 'No mobility session details available.'
        : first.session_type === 'Time' && firstDuration
            ? `Work through these ${count} movements, spending ${firstDuration} minute${Number(firstDuration) > 1 ? 's' : ''} on each.`
            : first.session_type === 'Movements'
                ? `Work through these ${count} movements, according to the movement instructions`
                : `Work through these ${count} movements.`;
    return (
        <>
            <p className="sw-summary">{summary}</p>
            {sessions.map((session, s) => (
                <div key={s} className="sw-section">
                    <Rows
                        items={session.mobility_details}
                        render={(detail) => {
                            const m = detail.movements;
                            const name = m?.exercise || 'Movement';
                            const eachSide = detail.movement_detail === 'Each side'
                                || (!detail.movement_detail && (m?.movement_type || '').toLowerCase() === 'unilateral');
                            const prefix = (session.session_type || '').toLowerCase() === 'time'
                                ? `${detail.duration ?? ''}'`
                                : `${detail.duration ?? ''} x`;
                            const url = m?.portrait_video_url || null;
                            return (
                                <StarRow
                                    bullet="•"
                                    name={`${prefix} ${name}${eachSide ? ' each side' : ''}`}
                                    video={url ? { title: name, url } : null}
                                    openVideo={openVideo}
                                />
                            );
                        }}
                    />
                </div>
            ))}
        </>
    );
}

function HiitBody({ sessions, openVideo }: { sessions: HiitSession[]; openVideo: OpenVideo }) {
    if (!sessions.length) return <Empty />;
    return (
        <>
            {sessions.map((session, s) => (
                <div key={s} className="sw-section-card">
                    {session.hiit_details.map((block, b) => (
                        <div key={b} className="sw-section">
                            {session.structure ? <p className="sw-section-sub sw-section-sub--top">{session.structure}</p> : null}
                            <Rows
                                mini
                                items={block.hiit_movements}
                                render={(mv, i) => {
                                    const url = mv.exercise_name === 'Rest' ? null : mv.movements?.portrait_video_url || null;
                                    const name = mv.exercise_name || '';
                                    return (
                                        <StarRow
                                            bullet={`${i + 1}:`}
                                            name={name}
                                            video={url ? { title: name, url } : null}
                                            openVideo={openVideo}
                                        />
                                    );
                                }}
                            />
                        </div>
                    ))}
                </div>
            ))}
        </>
    );
}

/* ─────────── Erg ─────────── */

const minsOrSecs = (s: number) => (s > 90 ? `${Math.round(s / 60)} mins` : `${Math.round(s)} secs`);

function ErgBody({ sessions, workoutName, openVideo }: { sessions: ErgSession[]; workoutName: string; openVideo: OpenVideo }) {
    if (!sessions.length) return <Empty />;
    return (
        <>
            {sessions.map((session, s) => {
                const url = videoOf(session.movement);
                const stacked = (session.repeat_type || '').toLowerCase() === 'stacked';
                const intervals = session.saved_intervals || [];
                return (
                    <div key={s} className="sw-block">
                        <div className="sw-block-head">
                            <h3 className="sw-block-title">{workoutName}</h3>
                            {url ? <PlayButton size={24} onClick={() => openVideo({ title: workoutName, url })} label="Machine video" /> : null}
                        </div>

                        {session.warmup_distance ? (
                            <>
                                <div className="sw-section">
                                    <h3 className="sw-section-title">Warm-up</h3>
                                    <p className="sw-detail">{session.warmup_distance} min warm-up</p>
                                </div>
                                <div className="sw-subdivider" />
                            </>
                        ) : null}

                        <div className="sw-section">
                            <h3 className="sw-section-title">Interval structure</h3>
                            {intervals.length ? (
                                <>
                                    {intervals.map((iv, i) => {
                                        const target = iv.target_pace == null ? null : String(iv.target_pace);
                                        const reps = iv.repeats || 1;
                                        const work = minsOrSecs(iv.repeat_distance || 0);
                                        return (
                                            <div key={i} className="sw-erg-step">
                                                <p className="sw-detail">
                                                    {stacked
                                                        ? ` • ${work}${target ? ` at ${target} pace` : ''}`
                                                        : ` * ${reps > 1 ? `${reps} × ` : ''}${work}${target ? ` at ${target} pace` : ''}`}
                                                </p>
                                                {iv.rest_time ? <p className="sw-erg-rest">Rest: {minsOrSecs(iv.rest_time)}</p> : null}
                                            </div>
                                        );
                                    })}
                                    {stacked ? <p className="sw-erg-repeat">Repeat {intervals[0]?.repeats || 1}x</p> : null}
                                </>
                            ) : (
                                <p className="sw-detail">No intervals defined</p>
                            )}
                        </div>

                        {session.cooldown_distance ? (
                            <>
                                <div className="sw-subdivider" />
                                <div className="sw-section">
                                    <h3 className="sw-section-title">Cool-down</h3>
                                    <p className="sw-detail">{session.cooldown_distance} min cool-down</p>
                                </div>
                            </>
                        ) : null}
                    </div>
                );
            })}
        </>
    );
}

/* ─────────── Running ─────────── */

const isNumber = (v: any): v is number => typeof v === 'number' && !Number.isNaN(v);

function formatDistance(m: number | null | undefined): string | null {
    if (!isNumber(m)) return null;
    if (m < 1000) return `${Math.round(m)}m`;
    const km = m / 1000;
    return `${Number.isInteger(km) ? km : km.toFixed(1)} km`;
}

/** Seconds per km as "5:30". */
function secsToClock(s: number): string {
    const total = Math.round(s);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function paceLabel(lo: number, hi: number | null = null): string {
    return hi === null || lo === hi ? `${secsToClock(lo)} mins/km` : `${secsToClock(lo)}–${secsToClock(hi)} mins/km`;
}

function paceRange(iv: RunningInterval, base: number): [number, number] {
    const lo = Number(iv.target_pace_lower) || base;
    const hi = Number(iv.target_pace_upper) || lo;
    return [Math.min(lo, hi), Math.max(lo, hi)];
}

type RestSpec = { unit: 'm' | 's' | 'none'; value: number };

function restSpec(iv: RunningInterval): RestSpec {
    const sec = (isNumber(iv.planned_rest_sec) ? iv.planned_rest_sec : null) ?? (isNumber(iv.rest_time) ? iv.rest_time : null) ?? 0;
    const m = isNumber(iv.planned_rest_m) ? iv.planned_rest_m : null;
    if (m != null && m > 0) return { unit: 'm', value: Math.round(m) };
    if (sec > 0) return { unit: 's', value: Math.round(sec) };
    return { unit: 'none', value: 0 };
}

function restLabel(spec: RestSpec | null, between = 'between intervals'): string | null {
    if (!spec || spec.unit === 'none' || !spec.value) return null;
    if (spec.unit === 'm') return `${formatDistance(spec.value)} jog ${between}`;
    return `${spec.value} seconds walking rest ${between}`;
}

function workLabel(iv: RunningInterval): string | null {
    if (isNumber(iv.planned_rep_m) && iv.planned_rep_m > 0) return formatDistance(iv.planned_rep_m);
    const sec = (isNumber(iv.planned_rep_sec) && iv.planned_rep_sec > 0 ? iv.planned_rep_sec : null)
        ?? (isNumber(iv.repeat_distance) && iv.repeat_distance > 0 ? iv.repeat_distance : null);
    if (sec != null) return sec > 90 ? `${(sec / 60).toFixed(0)} mins` : `${sec} secs`;
    return null;
}

function secsToMins(s: number): string {
    return s < 90 ? `${s} secs` : `${Math.floor(s / 60)} mins`;
}

function warmupLabel(s: RunningSession): string | null {
    if (isNumber(s.planned_warmup_m) && s.planned_warmup_m > 0) return formatDistance(s.planned_warmup_m);
    if (isNumber(s.planned_warmup_sec) && s.planned_warmup_sec > 0) return secsToMins(s.planned_warmup_sec);
    if (isNumber(s.warmup_distance) && s.warmup_distance > 0) return `${s.warmup_distance} min warm-up`;
    return null;
}

function cooldownLabel(s: RunningSession): string | null {
    if (isNumber(s.planned_cooldown_m) && s.planned_cooldown_m > 0) return formatDistance(s.planned_cooldown_m);
    if (isNumber(s.planned_cooldown_sec) && s.planned_cooldown_sec > 0) return secsToMins(s.planned_cooldown_sec);
    if (isNumber(s.cooldown_distance) && s.cooldown_distance > 0) return `${s.cooldown_distance} min cool-down`;
    return null;
}

function PacePill({ lo, hi = null, at = true }: { lo: number; hi?: number | null; at?: boolean }) {
    return (
        <div className="sw-pace-pill">
            {at ? <span className="sw-pace-at">at</span> : null}
            <span className="sw-pace-value">{paceLabel(lo, hi)}</span>
        </div>
    );
}

function RestLine({ text }: { text: string }) {
    return <div className="sw-rest-row"><span>{text}</span></div>;
}

function PreRunRoutine({ moves, openVideo }: { moves: MovementRef[]; openVideo: OpenVideo }) {
    const [open, setOpen] = useState(false);
    if (!moves.length) return null;
    return (
        <>
            <div className="sw-section">
                <button type="button" className="sw-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                    <span className="sw-section-title">Pre run routine</span>
                    {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {open ? (
                    <>
                        <p className="sw-section-sub">Work through these exercises one by one</p>
                        <Rows
                            items={moves}
                            render={(mv) => {
                                const url = videoOf(mv);
                                const name = mv?.exercise || '';
                                return <StarRow bullet="•" name={name} video={url ? { title: name, url } : null} openVideo={openVideo} />;
                            }}
                        />
                    </>
                ) : null}
            </div>
            <div className="sw-divider sw-divider--inset" />
        </>
    );
}

function isEasyRun(data: SharedWorkoutPayload): boolean {
    const name = String(data.name ?? '').toLowerCase();
    const desc = String(data.description ?? '').toLowerCase();
    return (name.includes('easy') && name.includes('run')) || (desc.includes('easy') && desc.includes('run'));
}

// The app's own fallback when there is no pace to go on (5:00/km).
const FALLBACK_PACE = 300;

function RunningBody({ data, openVideo }: { data: SharedWorkoutPayload; openVideo: OpenVideo }) {
    const sessions = data.running_sessions || [];
    if (!sessions.length) return <Empty />;
    const easy = isEasyRun(data);

    return (
        <>
            {sessions.map((session, s) => {
                const moves = (session.running_warmup_template?.movements || []).filter(Boolean);

                if (easy) {
                    const km = isNumber(session.total_distance)
                        ? (session.total_distance >= 1000 ? session.total_distance / 1000 : session.total_distance)
                        : null;
                    const stored = Number(session.suggested_warmup_pace);
                    const pace = Number.isFinite(stored) && stored > 0 ? stored : FALLBACK_PACE;
                    return (
                        <div key={s} className="sw-block">
                            <PreRunRoutine moves={moves} openVideo={openVideo} />
                            <div className="sw-section">
                                <p className="sw-easy">
                                    <strong>Run {km != null ? km.toFixed(1) : '-'} km</strong> at an easy pace of
                                </p>
                                <PacePill lo={pace} at={false} />
                            </div>
                        </div>
                    );
                }

                const base = (session.suggested_warmup_pace ?? 0) > 75 ? (session.suggested_warmup_pace as number) - 75 : FALLBACK_PACE;
                const distMode = session.prescription_mode === 'distance';
                const isRecovery = (iv: RunningInterval) =>
                    distMode && isNumber(iv.planned_rep_sec) && iv.planned_rep_sec > 0 && (!isNumber(iv.planned_rep_m) || iv.planned_rep_m === 0);
                const all = session.saved_intervals || [];
                const intervals = all.filter((iv) => !isRecovery(iv));
                const recoveryByVar: Record<number, number> = {};
                all.filter(isRecovery).forEach((r) => {
                    if (r.repeat_variation != null && r.planned_rep_sec != null) recoveryByVar[r.repeat_variation] = r.planned_rep_sec;
                });

                const rt = (session.repeat_type || '').toLowerCase();
                const grouped = rt === 'grouped';
                const groupedStacked = rt === 'grouped-stacked';
                const between = groupedStacked ? 'between rounds' : 'between intervals';
                const rounds = (grouped || groupedStacked) && intervals.length ? Math.max(...intervals.map((x) => Number(x.repeats || 0))) : 0;
                const specs = intervals.map(restSpec);
                const restVaries = new Set(specs.map((x) => `${x.unit}:${x.value}`)).size > 1;
                const showPerRest = groupedStacked ? false : restVaries;
                const shared = [...specs].reverse().find((x) => x.unit !== 'none' && x.value > 0) || null;
                const commonRest = !showPerRest && shared ? restLabel(shared, between) : null;

                const recoveryAfter = (iv: RunningInterval) => {
                    const sec = iv.repeat_variation != null ? recoveryByVar[iv.repeat_variation] : undefined;
                    if (!sec) return null;
                    return <RestLine text={sec >= 90 ? `${Math.round(sec / 60)} min recovery between sets` : `${sec} sec recovery between sets`} />;
                };

                const step = (iv: RunningInterval, i: number, title: string, rest: string | null) => {
                    const [lo, hi] = paceRange(iv, base);
                    return (
                        <div key={i}>
                            <p className="sw-interval-title">• {title}</p>
                            <PacePill lo={lo} hi={hi === lo ? null : hi} />
                            {rest ? <RestLine text={rest} /> : null}
                            {recoveryAfter(iv)}
                        </div>
                    );
                };

                const warm = warmupLabel(session);
                const cool = cooldownLabel(session);

                return (
                    <div key={s} className="sw-block">
                        <PreRunRoutine moves={moves} openVideo={openVideo} />

                        {warm ? (
                            <>
                                <div className="sw-section">
                                    <h3 className="sw-section-title sw-section-title--gap">Warm-up</h3>
                                    <p className="sw-interval-title">• {warm}</p>
                                    <PacePill lo={base} />
                                </div>
                                <div className="sw-subdivider" />
                            </>
                        ) : null}

                        <div className="sw-section">
                            {rt === 'standard' || grouped || groupedStacked ? (
                                <h3 className="sw-section-title sw-section-title--gap">Working sets</h3>
                            ) : null}
                            {rounds > 0 ? <p className="sw-repeat">Repeat {rounds}x</p> : null}
                            {intervals.length ? (
                                <>
                                    {intervals.map((iv, i) => {
                                        const work = workLabel(iv) || '';
                                        if (rt === 'standard') {
                                            const title = iv.repeats === 1 ? work : `${iv.repeats} × ${work}`;
                                            return step(iv, i, title.trim(), restLabel(restSpec(iv)));
                                        }
                                        if (grouped) return step(iv, i, work, showPerRest ? restLabel(restSpec(iv), between) : null);
                                        if (groupedStacked) return step(iv, i, work, null);
                                        const [lo, hi] = paceRange(iv, base);
                                        return (
                                            <div key={i} className="sw-fallback-step">
                                                <span className="sw-detail">• {work ? `${work} at` : 'at'}</span>
                                                <span className="sw-pace-value">{paceLabel(lo, hi === lo ? null : hi)}</span>
                                            </div>
                                        );
                                    })}
                                    {(grouped || groupedStacked) && commonRest ? <RestLine text={commonRest} /> : null}
                                </>
                            ) : (
                                <p className="sw-detail">No intervals defined</p>
                            )}
                        </div>

                        {cool ? (
                            <>
                                <div className="sw-subdivider" />
                                <div className="sw-section">
                                    <h3 className="sw-section-title sw-section-title--gap">Cool-down</h3>
                                    <p className="sw-interval-title">• {cool}</p>
                                    <PacePill lo={base} />
                                </div>
                            </>
                        ) : null}
                    </div>
                );
            })}
        </>
    );
}

/* ─────────── Rowing / Ready to Row ─────────── */

const range = (lo?: number | null, hi?: number | null) => (lo === hi || !hi ? `${lo}` : `${lo}-${hi}`);

const EFFORT_LABEL: Record<string, string> = {
    easy: 'Easy', steady: 'Steady', moderate: 'Moderate', moderate_hard: 'Moderate hard', hard: 'Hard',
};
const TALK_TEST_LABEL: Record<string, string> = {
    full_sentences: 'Full sentences', broken_sentences: 'Broken sentences', few_words: 'A few words', cant_talk: "Can't talk",
};
const BLOCK_LABEL: Record<string, string> = { warmup: 'Warm-up', main: 'Main set', cooldown: 'Cool-down' };

function formatWork(sec?: number | null, metres?: number | null): string {
    if (metres) return `${metres}m`;
    if (!sec) return '';
    if (sec < 120 && sec % 60 !== 0) return `${sec} sec`;
    const mins = sec / 60;
    return Number.isInteger(mins) ? `${mins} min` : `${mins.toFixed(1)} min`;
}

function stepTitle(step: RowingStep): string {
    const work = step.planned_rep_sec_upper
        ? `${range(step.planned_rep_sec! / 60, step.planned_rep_sec_upper / 60)} min`
        : formatWork(step.planned_rep_sec, step.planned_rep_m);
    const head = step.repeats > 1 ? `${step.repeats} x ${work}` : work;
    return `${head}${step.planned_rest_sec ? `, ${formatWork(step.planned_rest_sec)} rest` : ''}`;
}

function stepTargets(step: RowingStep): string[] {
    const lines: string[] = [];
    if (step.target_lower_pct) {
        lines.push(`${range(step.target_lower_pct, step.target_upper_pct)}% of 2k output`);
    }
    if (step.effort_label || step.rpe_lower) {
        const effort = step.effort_label ? EFFORT_LABEL[step.effort_label] ?? step.effort_label : null;
        const rpe = step.rpe_lower ? `RPE ${range(step.rpe_lower, step.rpe_upper)}` : null;
        lines.push([effort, rpe].filter(Boolean).join(' · '));
    }
    if (step.rate_lower) lines.push(`Rate ${range(step.rate_lower, step.rate_upper)}`);
    if (step.hr_lower_pct) {
        lines.push(`${lines.length && step.target_lower_pct ? 'or ' : ''}${range(step.hr_lower_pct, step.hr_upper_pct)}% max HR`);
    }
    if (step.talk_test) lines.push(`Talk test: ${(TALK_TEST_LABEL[step.talk_test] ?? step.talk_test).toLowerCase()}`);
    return lines;
}

function RowingSteps({ steps, title, hideTargets = false }: { steps: RowingStep[]; title: string; hideTargets?: boolean }) {
    return (
        <div className="sw-block">
            <h3 className="sw-row-session-title">{title}</h3>
            {steps.map((step, i) => {
                const targets = hideTargets ? [] : stepTargets(step);
                return (
                    <div key={i} className="sw-step-card">
                        <div className={step.block === 'main' ? 'sw-step-strip sw-step-strip--main' : 'sw-step-strip'} />
                        <div className="sw-step-body">
                            <p className="sw-step-label">{BLOCK_LABEL[step.block] ?? step.block}</p>
                            <p className="sw-step-title">{stepTitle(step)}</p>
                            {targets.length ? <p className="sw-step-target">{targets.join(' · ')}</p> : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function techniqueVideo(t: Technique | null): VideoTarget | null {
    if (!t) return null;
    return { title: t.title, url: t.portrait_video_url || t.landscape_video_url, cues: t.cues };
}

function ReadyToRowBody({ sessions, rtr, openVideo }: { sessions: RowingSession[]; rtr: ReadyToRow; openVideo: OpenVideo }) {
    const row = sessions.find((s) => s.machine === 'Rower');
    const bike = sessions.find((s) => s.machine === 'BikeErg');
    const warmup = (row?.saved_intervals || []).filter((s) => s.block === 'warmup');
    return (
        <>
            <div className="sw-section-card">
                <div className="sw-section">
                    <h3 className="sw-section-title">Overview</h3>
                    {rtr.focus ? <p className="sw-section-sub">{rtr.focus}</p> : null}
                    <Rows items={rtr.key_positions} render={(t) => <StarRow name={t.title} video={techniqueVideo(t)} openVideo={openVideo} />} />
                </div>
            </div>

            {warmup.length ? (
                <div className="sw-section-card">
                    <div className="sw-section">
                        <h3 className="sw-section-title sw-section-title--gap">Warm up</h3>
                        <Rows
                            items={warmup}
                            render={(step) => (
                                <div className="sw-row">
                                    <div className="sw-row-text">
                                        <span className="sw-row-bullet">*</span>
                                        <span className="sw-row-name">
                                            {stepTitle(step)}
                                            <span className="sw-row-detail">{stepTargets(step).join(' · ')}</span>
                                        </span>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>
            ) : null}

            <div className="sw-section-card">
                <div className="sw-section">
                    <h3 className="sw-section-title sw-section-title--gap">Drills</h3>
                    <Rows items={rtr.drills} render={(d) => <StarRow name={d.title} video={techniqueVideo(d.technique)} openVideo={openVideo} />} />
                </div>
            </div>

            {row ? <RowingSteps steps={row.saved_intervals.filter((s) => s.block !== 'warmup')} title="Row" hideTargets /> : null}
            {bike ? <RowingSteps steps={bike.saved_intervals} title="Bike" hideTargets /> : null}
        </>
    );
}

function RowingBody({ data, openVideo }: { data: SharedWorkoutPayload; openVideo: OpenVideo }) {
    const sessions = data.rowing_sessions || [];
    if (!sessions.length) return <Empty />;
    if (data.ready_to_row) return <ReadyToRowBody sessions={sessions} rtr={data.ready_to_row} openVideo={openVideo} />;
    return <RowingSteps steps={sessions[0].saved_intervals || []} title="Session" />;
}

/* ─────────── entry ─────────── */

export default function SharedWorkoutBody({ data, openVideo }: { data: SharedWorkoutPayload; openVideo: OpenVideo }) {
    const at = (data.activity_type || '').toLowerCase();
    const sections = data.workout_sections || data.sections || [];
    if (at === 'hiit') return <HiitBody sessions={data.hiit_sessions || []} openVideo={openVideo} />;
    if (at === 'mobility') return <MobilityBody sessions={data.mobility_sessions || []} openVideo={openVideo} />;
    if (at === 'hyrox') return <HyroxBody sections={sections} openVideo={openVideo} />;
    if (at === 'erg') return <ErgBody sessions={data.erg_sessions || []} workoutName={data.name || ''} openVideo={openVideo} />;
    if (at === 'running') return <RunningBody data={data} openVideo={openVideo} />;
    if (at === 'rowing') return <RowingBody data={data} openVideo={openVideo} />;
    return <GymBody sections={sections} openVideo={openVideo} />;
}
